import type { ReactElement } from 'react';
import { useRef, useState } from 'react';
import { Spinner } from '@savvy-kit/components/spinner';
import { Text } from '@savvy-kit/foundations/text';
import TrackedButton from 'src/components/tracked/tracked-button';

import GuidedFlowDoormat from './guided-flow-doormat';

type IntroDoormatProps = {
  readonly header: string;
  readonly body: string | ReactElement;
  readonly loading?: boolean;
  readonly error?: boolean;
  readonly onContinue?: () => void;
  readonly buttonText?: string;
  readonly onSecondary?: () => void;
  readonly secondaryButtonText?: string;
};

// Intro to a guided flow
function IntroDoormat(props: IntroDoormatProps) {
  const { header, body, loading, error, onContinue, buttonText, onSecondary, secondaryButtonText } = props;
  const [slide1, setSlide1] = useState(false);
  const [slide2, setSlide2] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerMaxHeight, setContainerMaxHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const slide1Ref = useRef<HTMLDivElement | null>(null);
  const slide2Ref = useRef<HTMLDivElement | null>(null);

  if (loading) {
    return (
      <GuidedFlowDoormat>
        <div className="my-4 flex flex-grow justify-center">
          <Spinner className="size-12" />
        </div>
      </GuidedFlowDoormat>
    );
  }

  if (error) {
    return (
      <GuidedFlowDoormat>
        <Text>Something went wrong. Please try again.</Text>
      </GuidedFlowDoormat>
    );
  }

  return (
    <GuidedFlowDoormat maxWidth={543}>
      <div
        ref={containerRef}
        style={{
          transition: 'max-height 500ms ease-out',
          maxHeight: containerMaxHeight,
        }}
      >
        <div
          ref={headerRef}
          className="animate-in slide-in-from-bottom-4 fill-mode-both duration-1000"
          // `clientHeight` reads the just-mounted element as its enter animation starts,
          // growing the container to match.
          onAnimationStart={(e) => {
            // Guard against bubbled animation events from any future animated child.
            if (e.target === e.currentTarget) {
              setContainerMaxHeight((prev) => prev + (headerRef.current?.clientHeight ?? 0));
            }
          }}
          onAnimationEnd={(e) => {
            if (e.target === e.currentTarget) setSlide1(true);
          }}
        >
          <Text variant="h2" className="text-center">
            {header}
          </Text>
        </div>
        {slide1 && (
          <div
            ref={slide1Ref}
            className="my-4 animate-in slide-in-from-bottom-4 fade-in-0 fill-mode-both duration-1000"
            onAnimationStart={(e) => {
              if (e.target === e.currentTarget) {
                setContainerMaxHeight((prev) => prev + (slide1Ref.current?.clientHeight ?? 0));
              }
            }}
            onAnimationEnd={(e) => {
              if (e.target === e.currentTarget) setSlide2(true);
            }}
          >
            <Text color="secondary" className="text-center" render={<div />}>
              {body}
            </Text>
          </div>
        )}
        {slide2 && (
          <div
            ref={slide2Ref}
            className="my-6 animate-in slide-in-from-bottom-4 fade-in-0 fill-mode-both duration-1000"
            onAnimationStart={(e) => {
              if (e.target === e.currentTarget) {
                setContainerMaxHeight((prev) => prev + (slide2Ref.current?.clientHeight ?? 0));
              }
            }}
          >
            <div className="flex flex-col items-center gap-2">
              <TrackedButton
                elementName="guided-flow-intro-doormat-continue-button"
                variant="secondary"
                onClick={onContinue}
              >
                {buttonText}
              </TrackedButton>
              {onSecondary !== undefined && secondaryButtonText !== undefined && (
                <TrackedButton
                  elementName="guided-flow-intro-doormat-secondary-button"
                  variant="link"
                  onClick={onSecondary}
                >
                  {secondaryButtonText}
                </TrackedButton>
              )}
            </div>
          </div>
        )}
      </div>
    </GuidedFlowDoormat>
  );
}

export default IntroDoormat;
